import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

import { createPrintBlobUrl, PRINT_PREVIEW_STORAGE_PREFIX } from './printPreviewTransport';

const PRINT_PREVIEW_WAIT_MS = 10000;
const PRINT_PREVIEW_POLL_MS = 100;

const readPrintPreviewHtml = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const jobId = params.get('job');
  if (!jobId) {
    return null;
  }

  const storageKey = `${PRINT_PREVIEW_STORAGE_PREFIX}${jobId}`;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { html?: unknown };
    window.localStorage.removeItem(storageKey);
    return typeof parsed.html === 'string' ? parsed.html : null;
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
};

export const PrintPreviewPage: React.FC = () => {
  const location = useLocation();
  const { t } = useSettings();
  const [status, setStatus] = useState<'loading' | 'missing'>('loading');

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const resolveHtml = () => {
      if (cancelled) {
        return;
      }

      const html = readPrintPreviewHtml(location.search);
      if (html) {
        window.location.replace(createPrintBlobUrl(html));
        return;
      }

      if (Date.now() - startedAt >= PRINT_PREVIEW_WAIT_MS) {
        setStatus('missing');
        return;
      }

      window.setTimeout(resolveHtml, PRINT_PREVIEW_POLL_MS);
    };

    resolveHtml();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  if (status === 'missing') {
    return (
      <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', color: '#111827' }}>
        {t('Print preview data is unavailable.')}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', color: '#111827' }}>
      {t('Preparing print preview…')}
    </div>
  );
};
